import { Injectable, inject } from '@angular/core';
import {
  Observable,
  map,
  catchError,
  throwError,
  of,
  switchMap,
  from,
} from 'rxjs';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import {
  LogicalWarehouse,
  LogicalWarehousePageResponse,
  LogicalWarehouseSearchParams,
  LogicalWarehouseCreateRequest,
  LogicalWarehouseUpdateRequest,
} from '../models/logical-warehouse.model';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  PageInfoWarehouseQueryResVO,
  WarehouseQueryResVO,
  WarehouseCreateWithAddressDTO,
  WarehouseDetailDTO,
  WarehouseCreateWithHierarchyDTO,
  WarehouseUpdateWithHierarchyDTO,
  WarehouseDetailWithHierarchyDTO,
} from '@app/api/stock-service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { OnlineShopControllerService } from '@app/api/channel-service/api/onlineShopController.service';

@Injectable({
  providedIn: 'root',
})
export class LogicalWarehouseService {
  private apiService = inject(WarehouseControllerService);
  private onlineShopControllerService = inject(OnlineShopControllerService);

  /**
   * Get logical warehouses with pagination
   */
  getLogicalWarehouses(
    params: LogicalWarehouseSearchParams,
  ): Observable<LogicalWarehousePageResponse> {
    // Prepare name for partial matching
    // If name is provided and not empty, ensure it supports partial matching
    // Most backends support partial matching automatically, but we ensure the value is trimmed
    let searchName: string | undefined = params.name?.trim();
    if (searchName && searchName.length > 0) {
      // Keep the name as-is - backend should handle partial matching
      // If backend requires wildcards, they would be added here
      // For now, assume backend supports partial matching with trimmed input
      searchName = searchName;
    } else {
      searchName = undefined;
    }

    // Prepare code array for partial matching
    // If code is provided, ensure it supports partial matching
    let searchCodes: string[] | undefined = undefined;
    if (params.code && params.code.length > 0) {
      // Filter out empty strings and trim each code
      searchCodes = params.code
        .map((c) => c?.trim())
        .filter((c) => c && c.length > 0);
      if (searchCodes.length === 0) {
        searchCodes = undefined;
      }
    }

    const queryDTO: WarehouseQueryReqDTO = {
      page: params.page,
      size: params.size,
      name: searchName,
      code: searchCodes,
      typeCodes: params.typeCodes,
      status: params.status,
      sortBy: 'creationDate',
      ascending: false,
    };

    return this.apiService.queryLogicalWarehouse(queryDTO).pipe(
      switchMap(
        (
          response:
            | PageInfoWarehouseQueryResVO
            | HttpResponse<PageInfoWarehouseQueryResVO>
            | Blob,
        ) => {
          // Handle Blob response
          if (response instanceof Blob) {
            return new Observable<PageInfoWarehouseQueryResVO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                  observer.complete();
                } catch (error) {
                  console.error('Error parsing Blob to JSON:', error);
                  observer.error(error);
                }
              };
              reader.onerror = (error) => {
                console.error('Error reading Blob:', error);
                observer.error(error);
              };
              reader.readAsText(response);
            });
          }
          // Handle HttpResponse wrapper
          if (response instanceof HttpResponse) {
            return of(response.body!);
          }
          // Handle direct PageInfoWarehouseQueryResVO
          return of(response as PageInfoWarehouseQueryResVO);
        },
      ),
      map((pageInfo: PageInfoWarehouseQueryResVO) => {
        const warehouses: LogicalWarehouse[] =
          (pageInfo.pageContent || []).map((item: WarehouseQueryResVO) =>
            this.mapWarehouseQueryResVOToLogicalWarehouse(item),
          ) || [];

        return {
          content: warehouses,
          totalElements: pageInfo.totalElements || 0,
          totalPages: pageInfo.totalPages || 0,
          size: pageInfo.size || params.size,
          number: pageInfo.number || params.page,
          first: (pageInfo.number || 0) === 0,
          last: (pageInfo.number || 0) >= (pageInfo.totalPages || 1) - 1,
          numberOfElements: pageInfo.numberOfElements || 0,
        };
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading logical warehouses:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get logical warehouse by ID
   */
  getLogicalWarehouseById(id: number): Observable<LogicalWarehouse> {
    return this.apiService.getWarehouseDetail(id).pipe(
      switchMap((response: any) => {
        // Handle Blob response
        if (response instanceof Blob) {
          return new Observable<any>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result as string);
                observer.next(json);
                observer.complete();
              } catch (error) {
                console.error('Error parsing Blob to JSON:', error);
                observer.error(error);
              }
            };
            reader.onerror = (error) => {
              console.error('Error reading Blob:', error);
              observer.error(error);
            };
            reader.readAsText(response);
          });
        }
        // Handle HttpResponse wrapper
        if (response instanceof HttpResponse) {
          return of(response.body!);
        }
        // Handle direct response
        return of(response);
      }),
      map((warehouse: any) =>
        this.mapWarehouseDetailDTOToLogicalWarehouse(warehouse),
      ),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading logical warehouse:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get logical warehouse with hierarchy by ID
   */
  getLogicalWarehouseWithHierarchyById(
    id: number,
  ): Observable<WarehouseDetailWithHierarchyDTO> {
    return this.apiService.getWarehouseDetailWithHierarchy(id).pipe(
      switchMap((response: any) => {
        // Handle Blob response
        if (response instanceof Blob) {
          return new Observable<WarehouseDetailWithHierarchyDTO>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(reader.result as string);
                observer.next(json);
                observer.complete();
              } catch (error) {
                console.error('Error parsing Blob to JSON:', error);
                observer.error(error);
              }
            };
            reader.onerror = (error) => {
              console.error('Error reading Blob:', error);
              observer.error(error);
            };
            reader.readAsText(response);
          });
        }
        // Handle HttpResponse wrapper
        if (response instanceof HttpResponse) {
          return of(response.body!);
        }
        // Handle direct response
        return of(response);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading logical warehouse with hierarchy:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Create logical warehouse
   */
  createLogicalWarehouse(
    warehouse: LogicalWarehouseCreateRequest,
  ): Observable<string> {
    const createDTO: WarehouseCreateWithAddressDTO = {
      tenantId: warehouse.tenantId,
      name: warehouse.name,
      code: warehouse.code,
      typeCode: warehouse.typeCode,
      status: warehouse.status ?? true,
      provinceCode: warehouse.provinceCode,
      provinceName: warehouse.provinceName,
      cityCode: warehouse.cityCode,
      cityName: warehouse.cityName,
      districtCode: warehouse.districtCode,
      districtName: warehouse.districtName,
      detailAddress: warehouse.detailAddress,
      lng: warehouse.lng,
      lat: warehouse.lat,
      contactName: warehouse.contactName,
      contactPhone: warehouse.contactPhone,
      description: warehouse.description,
      zoneIds: warehouse.zoneIds,
    };

    return this.apiService
      .createWarehouse(createDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response))
            return from(BlobConverterUtil.convertToJson(response));
          return from(Promise.resolve(response));
        }),
        catchError((error: HttpErrorResponse) => {
          if ([200, 201].includes(error.status)) return of(error.message);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Create logical warehouse with hierarchy
   */
  createLogicalWarehouseWithHierarchy(
    warehouse: WarehouseCreateWithHierarchyDTO,
  ): Observable<string> {
    return this.apiService
      .createWarehouseWithHierarchy(warehouse, 'body', false, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response))
            return from(BlobConverterUtil.convertToJson(response));
          return from(Promise.resolve(response));
        }),
        catchError((error: HttpErrorResponse) => {
          if ([200, 201].includes(error.status)) return of(error.message);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Update logical warehouse
   */
  updateLogicalWarehouse(
    warehouse: LogicalWarehouseUpdateRequest,
  ): Observable<string> {
    const updateDTO: WarehouseCreateWithAddressDTO = {
      tenantId: warehouse.tenantId,
      name: warehouse.name,
      code: warehouse.code,
      typeCode: warehouse.typeCode,
      status: warehouse.status ?? true,
      provinceCode: warehouse.provinceCode,
      provinceName: warehouse.provinceName,
      cityCode: warehouse.cityCode,
      cityName: warehouse.cityName,
      districtCode: warehouse.districtCode,
      districtName: warehouse.districtName,
      detailAddress: warehouse.detailAddress,
      lng: warehouse.lng,
      lat: warehouse.lat,
      contactName: warehouse.contactName,
      contactPhone: warehouse.contactPhone,
      description: warehouse.description,
      zoneIds: warehouse.zoneIds,
    };

    return this.apiService
      .updateWarehouse(warehouse.id, updateDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response))
            return from(BlobConverterUtil.convertToJson(response));
          return from(Promise.resolve(response));
        }),
        catchError((error: HttpErrorResponse) => {
          if ([200, 201].includes(error.status)) return of(error.message);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Update logical warehouse with hierarchy
   */
  updateLogicalWarehouseWithHierarchy(
    id: number,
    warehouse: WarehouseUpdateWithHierarchyDTO,
  ): Observable<string> {
    return this.apiService
      .updateWarehouseWithHierarchy(id, warehouse, 'body', false, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response))
            return from(BlobConverterUtil.convertToJson(response));
          return from(Promise.resolve(response));
        }),
        catchError((error: HttpErrorResponse) => {
          if ([200, 201].includes(error.status)) return of(error.message);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Delete logical warehouses by IDs
   */
  deleteLogicalWarehouses(ids: number[]): Observable<void> {
    if (!ids || ids.length === 0) {
      return throwError(() => new Error('No warehouse IDs provided'));
    }

    return this.apiService
      .deleteWarehouse(ids, 'body', false, {
        httpHeaderAccept: 'application/json' as '*/*',
      })
      .pipe(
        map(() => {
          return;
        }),
        catchError((error: HttpErrorResponse) => {
          // The endpoint answers 200 with a plain-text body, which the JSON responseType cannot parse.
          if ([200, 201].includes(error.status)) return of(undefined);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Map WarehouseQueryResVO to LogicalWarehouse
   */
  private mapWarehouseQueryResVOToLogicalWarehouse(
    item: WarehouseQueryResVO,
  ): LogicalWarehouse {
    // Extract address fields that might exist in the actual API response
    // even if they're not in the TypeScript interface
    const itemAny = item as any;
    return {
      id: item.id,
      name: item.name || '',
      code: item.code || '',
      typeCode: item.typeCode || '',
      status: item.status ?? true,
      countryId: itemAny.countryId || itemAny.countryCode || undefined,
      countryName: itemAny.countryName || itemAny.country || undefined,
      provinceCode: itemAny.provinceCode || 0,
      provinceName: itemAny.provinceName || itemAny.province || '',
      cityCode: itemAny.cityCode || 0,
      cityName: itemAny.cityName || itemAny.city || '',
      districtCode: itemAny.districtCode || itemAny.countyCode || 0,
      districtName:
        itemAny.districtName ||
        itemAny.countyName ||
        itemAny.district ||
        itemAny.county ||
        '',
      detailAddress: item.detailAddress || '',
      contactName: item.contactName,
      contactPhone: item.contactPhone,
      zoneIds: itemAny.zoneIds || [],
      creationDate: item.creationDate,
      lastUpdateDate: item.lastUpdateDate,
    };
  }

  /**
   * Map WarehouseDetailDTO to LogicalWarehouse
   */
  private mapWarehouseDetailDTOToLogicalWarehouse(
    dto: WarehouseDetailDTO,
  ): LogicalWarehouse {
    // Extract country fields that might exist in the actual API response
    const dtoAny = dto as any;
    return {
      id: dto.id,
      name: dto.name || '',
      code: dto.code || '',
      typeCode: dto.typeCode || '',
      status: dto.status ?? true,
      tenantId: dto.tenantId,
      countryId: dtoAny.countryId || dtoAny.countryCode || undefined,
      countryName: dtoAny.countryName || dtoAny.country || undefined,
      provinceCode: dto.provinceCode || 0,
      provinceName: dto.provinceName || '',
      cityCode: dto.cityCode || 0,
      cityName: dto.cityName || '',
      districtCode: dto.districtCode || 0,
      districtName: dto.districtName || '',
      detailAddress: dto.detailAddress || '',
      lng: dto.lng,
      lat: dto.lat,
      contactName: dto.contactName,
      contactPhone: dto.contactPhone,
      description: dto.description,
      zoneIds: dto.zoneIds || [],
      length: dto.length,
      width: dto.width,
      height: dto.height,
      weight: dtoAny.weight,
    };
  }

  /**
   * Check if warehouse IDs are associated with any online shops
   */
  checkWarehouseAssociation(warehouseIds: number[]): Observable<boolean> {
    return this.onlineShopControllerService.checkWarehouseAssociation(
      warehouseIds,
      'body',
      false,
      {
        httpHeaderAccept: 'application/json' as '*/*',
      },
    );
  }
}
